import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createPatientPayment,
  deletePatientPayment,
  deletePatientPaymentInvoice,
  listPatientPayments,
  setPatientPaymentInvoice,
  updatePatientPayment,
  type CreatePatientPaymentPayload,
  type UpdatePatientPaymentPayload,
} from '../../../api/patient-payments';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

async function invalidatePatientPaymentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['patient-payments', patientId] }),
    queryClient.invalidateQueries({ queryKey: ['treatments', patientId] }),
    queryClient.invalidateQueries({ queryKey: ['patient', patientId] }),
  ]);
}

export function useListPatientPayments(patientId: string) {
  const { tokens } = useAuth();

  return useQuery({
    queryKey: ['patient-payments', patientId],
    queryFn: () => listPatientPayments(patientId, tokens!.access_token),
    enabled: Boolean(patientId && tokens?.access_token),
  });
}

export function useCreatePatientPayment(patientId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePatientPaymentPayload) =>
      createPatientPayment(patientId, payload, getToken()),
    onSuccess: () => invalidatePatientPaymentQueries(queryClient, patientId),
  });
}

export function useUpdatePatientPayment(patientId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { paymentId: string; payload: UpdatePatientPaymentPayload }) =>
      updatePatientPayment(patientId, args.paymentId, args.payload, getToken()),
    onSuccess: () => invalidatePatientPaymentQueries(queryClient, patientId),
  });
}

export function useSetPatientPaymentInvoice(patientId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { paymentId: string; file: File }) =>
      setPatientPaymentInvoice(patientId, args.paymentId, args.file, getToken()),
    onSuccess: () => invalidatePatientPaymentQueries(queryClient, patientId),
  });
}

export function useDeletePatientPayment(patientId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentId: string) => deletePatientPayment(patientId, paymentId, getToken()),
    onSuccess: () => invalidatePatientPaymentQueries(queryClient, patientId),
  });
}

export function useDeletePatientPaymentInvoice(patientId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentId: string) =>
      deletePatientPaymentInvoice(patientId, paymentId, getToken()),
    onSuccess: () => invalidatePatientPaymentQueries(queryClient, patientId),
  });
}
